import {
  ApiError,
  factoryShiftPatternsControllerArchiveOverride,
  factoryShiftPatternsControllerArchivePattern,
  factoryShiftPatternsControllerCreateOverride,
  factoryShiftPatternsControllerCreatePattern,
  factoryShiftPatternsControllerGetCalendar,
  factoryShiftPatternsControllerListOverrides,
  factoryShiftPatternsControllerListPatterns,
  factoryShiftPatternsControllerUnarchivePattern,
  factoryShiftPatternsControllerUpdateOverride,
  factoryShiftPatternsControllerUpdatePattern,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import { PROJECT_PLANNING_KEY } from '../project-planning/queries';
import type {
  CalendarDay,
  CreateOverrideInput,
  CreateShiftPatternInput,
  ShiftOverride,
  ShiftPattern,
  UpdateOverrideInput,
  UpdateShiftPatternInput,
} from './types';

export const SHIFT_PATTERNS_KEY = 'shift-patterns' as const;

// ── Conflict surface (409 → field-pinned error) ───────────────────────────────
//
// Overlapping pattern date ranges come back as a 409 ConflictException with
// body `{ field: 'start_date', message }` — the same shape factory-admin maps
// for duplicate names. The editor pins the message to the start_date field.

export type ShiftPatternConflictField = 'start_date';

export class ShiftPatternConflictError extends Error {
  public readonly field: ShiftPatternConflictField;

  constructor(field: ShiftPatternConflictField, message: string) {
    super(message);
    this.name = 'ShiftPatternConflictError';
    this.field = field;
  }
}

function mapConflict(err: unknown): never {
  if (err instanceof ApiError && err.status === 409) {
    const body = err.bodyAsRecord;
    const rawField = body?.['field'];
    const rawMessage = body?.['message'];
    const message = typeof rawMessage === 'string' ? rawMessage : err.message;
    if (rawField === 'start_date') {
      throw new ShiftPatternConflictError(rawField, message);
    }
  }
  throw err;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function shiftPatternsQueryOptions(factoryId: string | undefined) {
  return queryOptions({
    queryKey: [SHIFT_PATTERNS_KEY, 'patterns', factoryId] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await factoryShiftPatternsControllerListPatterns(factoryId, { signal });
      return expectData<ShiftPattern[]>(res);
    },
    enabled: !!factoryId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function shiftOverridesQueryOptions(factoryId: string | undefined) {
  return queryOptions({
    queryKey: [SHIFT_PATTERNS_KEY, 'overrides', factoryId] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await factoryShiftPatternsControllerListOverrides(factoryId, undefined, {
        signal,
      });
      return expectData<ShiftOverride[]>(res);
    },
    enabled: !!factoryId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * The factory's effective shift calendar over a date range — one entry per day
 * carrying `is_working`. Used by the Reschedule dialog to grey out non-working
 * days (ENG-902); the range is inclusive and capped at 400 days server-side.
 *
 * Note the endpoint fills EVERY day in the range, reporting `is_working: false`
 * for days no pattern covers. An all-false response therefore means "this
 * factory has no shift pattern here", not "this factory never works" — callers
 * must not read it as a reason to disable the whole calendar.
 */
export function factoryCalendarQueryOptions(
  factoryId: string | null | undefined,
  from: string,
  to: string,
) {
  return queryOptions({
    queryKey: [SHIFT_PATTERNS_KEY, 'calendar', factoryId, from, to] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await factoryShiftPatternsControllerGetCalendar(
        factoryId,
        { from, to },
        { signal },
      );
      return expectData<CalendarDay[]>(res);
    },
    enabled: !!factoryId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

function useInvalidate(factoryId: string) {
  const queryClient = useQueryClient();
  const invalidateLaborSummaries = () =>
    void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'labor-summary'] });
  return {
    patterns: () => {
      invalidateLaborSummaries();
      void queryClient.invalidateQueries({
        queryKey: [SHIFT_PATTERNS_KEY, 'patterns', factoryId],
      });
    },
    overrides: () => {
      invalidateLaborSummaries();
      void queryClient.invalidateQueries({
        queryKey: [SHIFT_PATTERNS_KEY, 'overrides', factoryId],
      });
    },
  };
}

export function useCreatePattern(factoryId: string) {
  const invalidate = useInvalidate(factoryId);
  return useMutation({
    mutationFn: async (input: CreateShiftPatternInput) => {
      try {
        const res = await factoryShiftPatternsControllerCreatePattern(factoryId, input);
        return expectData<ShiftPattern>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.patterns(),
  });
}

/** patternId travels in the input so the editor can also PATCH the *previous* pattern (fix-it hint). */
export function useUpdatePattern(factoryId: string) {
  const invalidate = useInvalidate(factoryId);
  return useMutation({
    mutationFn: async ({
      patternId,
      input,
    }: {
      patternId: string;
      input: UpdateShiftPatternInput;
    }) => {
      try {
        const res = await factoryShiftPatternsControllerUpdatePattern(factoryId, patternId, input);
        return expectData<ShiftPattern>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.patterns(),
  });
}

export function useSetPatternArchived(factoryId: string) {
  const invalidate = useInvalidate(factoryId);
  return useMutation({
    mutationFn: async ({ patternId, archived }: { patternId: string; archived: boolean }) => {
      if (archived) {
        await factoryShiftPatternsControllerArchivePattern(factoryId, patternId);
      } else {
        try {
          await factoryShiftPatternsControllerUnarchivePattern(factoryId, patternId);
        } catch (err) {
          // Unarchiving back into a taken date range conflicts too.
          mapConflict(err);
        }
      }
    },
    onSuccess: () => invalidate.patterns(),
  });
}

export function useCreateOverride(factoryId: string) {
  const invalidate = useInvalidate(factoryId);
  return useMutation({
    mutationFn: async (input: CreateOverrideInput) => {
      const res = await factoryShiftPatternsControllerCreateOverride(factoryId, input);
      return expectData<ShiftOverride>(res);
    },
    onSuccess: () => invalidate.overrides(),
  });
}

export function useUpdateOverride(factoryId: string) {
  const invalidate = useInvalidate(factoryId);
  return useMutation({
    mutationFn: async ({
      overrideId,
      input,
    }: {
      overrideId: string;
      input: UpdateOverrideInput;
    }) => {
      const res = await factoryShiftPatternsControllerUpdateOverride(factoryId, overrideId, input);
      return expectData<ShiftOverride>(res);
    },
    onSuccess: () => invalidate.overrides(),
  });
}

export function useArchiveOverride(factoryId: string) {
  const invalidate = useInvalidate(factoryId);
  return useMutation({
    mutationFn: async (overrideId: string) => {
      await factoryShiftPatternsControllerArchiveOverride(factoryId, overrideId);
    },
    onSuccess: () => invalidate.overrides(),
  });
}
